<template>
    <div>
        <div v-if="type.global">
            <div class="is-align-center auto-save">
                <div v-if="hasErrors && !saving" class="button auto-save__global__status">
                    <span class="has-text-danger">Save failed — Retry</span>

                    <e-icon icon="exclamation-triangle" class="has-text-danger" />
                </div>

                <div v-else-if="!isEmpty" class="button auto-save__global__status tw:flex tw:items-center tw:gap-2">
                    <ProgressSpinner
                        v-if="saving || courseKeyIntegrationInProgress"
                        stroke-width="4"
                        class="tw:w-4 tw:h-4"
                    />
                    <span>{{ message }}</span>

                    <e-icon v-if="saved && !hasErrors" icon="check" class="has-text-success" />
                </div>
            </div>
        </div>

        <div v-else>
            <div v-if="type.header">
                <div v-show="status" class="has-text-weight-normal has-text-danger tw:whitespace-nowrap">
                    <small>*Missing Required Information</small>
                </div>
            </div>

            <div v-if="!type.header" class="is-flex" :class="{ 'is-block': type.list }">
                <div
                    v-for="item in status"
                    :key="item"
                    class="has-text-weight-normal has-text-danger"
                    :class="{ 'm-none': type.list }"
                >
                    <small>{{ item }}</small>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import filter from 'lodash/filter.js';
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';
import values from 'lodash/values.js';
import ProgressSpinner from 'primevue/progressspinner';
import { createNamespacedHelpers } from 'vuex';

const { mapState, mapGetters } = createNamespacedHelpers('AutoSave');

export default {
    name: 'AutoSave',

    components: { ProgressSpinner },

    props: {
        type: {
            default: () => {
                return {
                    global: true,
                };
            },
            type: Object,
            required: false,
        },

        indicator: {
            default: null,
            required: false,
            type: String,
        },

        status: {
            default: null,
            required: false,
            type: Object,
        },

        id: {
            type: [String, Number],
            required: false,
            default: null,
        },
    },

    computed: {
        ...mapState({
            indicators: state => state.indicators,
            autosaveErrors: 'errors',
        }),

        ...mapGetters(['isEmpty', 'validationErrors']),

        saving() {
            if (this.indicator) {
                const saver = this.indicators[this.indicator];

                return get(saver, 'message') === 'Saving...';
            }
            return filter(values(this.indicators), item => item.saved === false).length > 0;
        },

        message() {
            if (this.indicator) {
                const saver = this.indicators[this.indicator];

                return get(saver, 'message');
            }

            return get(values(this.indicators), '0.message');
        },

        saved() {
            if (this.indicator) {
                return get(this.indicators[this.indicator], 'saved');
            }

            return this.$store.getters['AutoSave/allSaved'];
        },

        /**
         * Check if there are any autosave errors.
         *
         * For the global indicator (type.global), this intentionally shows "Save failed — Retry"
         * for ANY autosave error without filtering by indicator or activeId. This ensures
         * validation errors are always visible to the user regardless of which field
         * triggered the error.
         */
        hasErrors() {
            return !isEmpty(this.autosaveErrors);
        },

        active() {
            const activeId = this.$store.getters['AutoSave/getActiveId'];

            return this.id === activeId;
        },

        courseKeyIntegrationInProgress() {
            return this.$store.state.CourseTemplateForm?.courseKeyIntegrationInProgress || false;
        },
    },

    watch: {
        validationErrors(list) {
            if (list.length > 0) {
                // Show validation errors with field context using snackbar for better visibility
                list.forEach(error => {
                    const message = typeof error === 'string' ? error : error.formatted || error.message;

                    // Only show snackbar if message is a non-empty string
                    if (message && typeof message === 'string' && message.trim()) {
                        this.$snackbar.open({
                            duration: 15000,
                            message: `Validation Error: ${message}`,
                            position: 'is-bottom-left',
                            type: 'is-danger',
                            actionText: 'Dismiss',
                        });
                    }
                });
            }
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';

.auto-save {
    // Reserve enough width for the widest save-cycle state ("Saving..." + spinner) so the
    // right-aligned header indicator stays put as it transitions idle -> saving -> saved.
    min-width: 170px;
    min-height: 60px;
    display: inline-flex;
    align-items: center;
}

.auto-save__global__status {
    border: 0 !important;
    cursor: unset !important;

    &:hover {
        color: $text !important;
    }

    .icon i::before {
        position: relative;
        top: -2px;
    }
}
</style>
